import { useDroppable, useDraggable } from '@dnd-kit/core';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { CalendarNote } from '@/types/calendar-notes';
import { CampaignDayPosition } from '@/utils/campaignMatcher';
import { CalendarHoliday } from '@/types/holidays';
import { getContrastColor } from '@/utils/colors';
import { Tooltip } from '@/components/ui/Tooltip';
import { Icon } from '@/components/ui/Icon';
import { RetryImage } from '@/components/ui';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { HoverCard } from '@/components/ui/HoverCard';
import { ICONS } from '@/constants/icons';
import { Post } from '@/services/posts';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useCalendarSettingsStore } from '@/stores/calendarSettingsStore';
import { usePostPermissions } from '@/hooks/usePostPermissions';
import { MoonPhaseIndicator } from './MoonPhase';
import { QuickAddMenu } from './QuickAddMenu';
import { getInferredStatus } from '@/utils/calendarStatus';
import { CalendarPostPopover } from './PostPopover/CalendarPostPopover';

/**
 * CalendarDayCell Component
 *
 * Individual day cell in the calendar grid.
 * Shows:
 * - Date number
 * - Mini post thumbnails with time and status icons
 * - Hover popover for details
 *
 * Click opens the day detail panel.
 * Also acts as a droppable zone for drag-and-drop.
 */

interface CalendarDayCellProps {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  postCount: number;
  posts: Post[];
  onClick: (date: Date) => void;
  onAddPost?: (date: Date) => void;
  onAddCampaign?: (date: Date) => void;
  onAddContentPillar?: (date: Date) => void;
  isDraggingActive?: boolean;
  campaigns?: CalendarNote[];
  campaignPositions?: Map<number, CampaignDayPosition>;
  /** Global slot assignment map (campaignId → slot index) for consistent vertical positioning */
  campaignSlots?: Map<number, number>;
  holidays?: CalendarHoliday[];
  onCampaignClick?: (campaign: CalendarNote) => void;
  onPostClick?: (post: Post) => void;
  onPostDelete?: (post: Post) => void;
  /** Map of calendarId → { name, color } for tooltip display */
  calendarsById?: Map<number, { name: string; color: string }>;
}

// Mini Post Thumbnail Component (Draggable)
const MiniPostThumb = ({
  post,
  date,
  formatTime,
  isDraggingGlobal = false,
  onPostClick,
  onPostDelete,
}: {
  post: Post;
  date: Date;
  formatTime: (date: string | null) => string | null;
  isDraggingGlobal?: boolean;
  onPostClick?: (post: Post) => void;
  onPostDelete?: (post: Post) => void;
}) => {
  const navigate = useNavigate();
  const { canEditPost } = usePostPermissions();
  const firstMedia = post.postMedia?.[0];
  const hasMedia = firstMedia && (firstMedia.thumbnailUrl || firstMedia.originalUrl);
  const time = post.scheduledAt ? formatTime(post.scheduledAt) : null;

  const isBlog = post.source === 'blog';

  // Allow dragging for MAPPED (not yet committed) and SCHEDULED (committed but not yet published)
  // In-flight (PUBLISHING) and completed (PUBLISHED, FAILED) posts cannot be moved
  // Blog entries are READ-ONLY: never draggable/clickable-to-edit
  const inferredStatus = getInferredStatus(post);
  const canDrag = !isBlog && (inferredStatus === 'MAPPED' || inferredStatus === 'SCHEDULED');

  // mappingId is set per-entry from calendar endpoint, ensuring uniqueness for multi-mapped posts
  const mappingId = post.mappingId || post.calendarMappings?.[0]?.id;

  // Make post draggable (if allowed)
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    // Use mappingId in ID to ensure uniqueness when same post has multiple mappings.
    // Blog entries use a namespaced id so they never collide with a real social post's id.
    id: isBlog ? `blog-post-${post.blogPostId}` : `calendar-post-${mappingId || post.id}`,
    data: {
      type: 'calendar-post',
      post,
      mappingId, // Include specific mappingId for move operations
      sourceDate: date.toISOString(),
    },
    disabled: !canDrag || isBlog,
  });

  // Mapped items stay openable for read-only review/scheduling; queued items
  // open for editing only while their content remains unlocked.
  const canEdit = !isBlog && (inferredStatus === 'MAPPED' || (inferredStatus === 'SCHEDULED' && canEditPost(post)));
  const canClick = isBlog || (canEdit && !!onPostClick);

  const thumbContent = (
    <div
      ref={setNodeRef}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      onClick={(e) => {
        if (isBlog) {
          e.stopPropagation();
          navigate(`/blog/${post.blogPostId}`);
          return;
        }
        if (canClick) {
          e.stopPropagation();
          onPostClick!(post);
        }
      }}
      className={cn(
        "relative w-7 h-7 sm:w-9 sm:h-9 md:w-11 md:h-11 rounded overflow-hidden bg-gray-100 flex-shrink-0 transition-all",
        canDrag && !isDragging && "cursor-grab hover:scale-105",
        !canDrag && canClick && "cursor-pointer hover:scale-105",
        !canDrag && !canClick && "cursor-default",
        isDragging && "opacity-50 scale-95 cursor-grabbing",
        isDraggingGlobal && !isDragging && "opacity-60"
      )}
    >
      {/* Thumbnail */}
      {hasMedia ? (
        <RetryImage
          src={firstMedia.thumbnailUrl || firstMedia.originalUrl || undefined}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Blog badge (top-right) — distinguishes read-only WordPress blog chips from social posts */}
      {isBlog && (
        <div className="absolute top-0 right-0 w-3 h-3 sm:w-3.5 sm:h-3.5 flex items-center justify-center bg-white/90 rounded-bl">
          <Icon name={ICONS.BLOG} size="xs" className="text-gray-600" ariaHidden />
        </div>
      )}

      {/* Time indicator */}
      {time && (
        <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white flex items-center justify-center py-px">
          <span className="text-[6px] sm:text-[7px] md:text-[8px] font-medium leading-none">{time}</span>
        </div>
      )}
    </div>
  );

  // Disable hover card during any drag interaction to avoid z-index conflicts
  if (isDragging || isDraggingGlobal) {
    return thumbContent;
  }

  return (
    <HoverCard
      trigger={thumbContent}
      side="left"
      align="start"
      sideOffset={8}
      openDelay={200}
      closeDelay={150}
    >
      <CalendarPostPopover
        post={post}
        inferredStatus={inferredStatus}
        onEdit={onPostClick}
        onDelete={onPostDelete}
      />
    </HoverCard>
  );
};


export const CalendarDayCell = ({
  date,
  isCurrentMonth,
  isToday,
  isSelected,
  postCount,
  posts,
  onClick,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
  isDraggingActive = false,
  campaigns = [],
  campaignPositions,
  campaignSlots,
  holidays = [],
  onCampaignClick,
  onPostClick,
  onPostDelete,
  calendarsById,
}: CalendarDayCellProps) => {
  const { t } = useTranslation(['calendar', 'common']);
  const { formatTimeOnly } = useTimezone();

  const { showMoonPhases } = useCalendarSettingsStore();
  const dayNumber = date.getDate();
  const hasContent = postCount > 0;

  // Helper to check if a date is in the past (comparing just the date, not time)
  const isPastDate = (checkDate: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(checkDate);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  const isPast = isPastDate(date);

  // Set up droppable zone
  const { isOver, setNodeRef } = useDroppable({
    id: `day-${date.toISOString()}`,
    data: {
      type: 'calendar-day',
      date,
    },
    disabled: isPast, // Disable dropping on past dates
  });

  const isValidDropTarget = isDraggingActive && isCurrentMonth && !isPast;
  const isHoveringOver = isOver;

  // Sort posts by scheduled time
  const sortedPosts = [...posts].sort((a, b) => {
    if (!a.scheduledAt) return 1;
    if (!b.scheduledAt) return -1;
    return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
  });

  // Show up to 3 posts in a 2-col wrap grid, with "+N" as 4th slot if more
  const maxVisible = posts.length > 3 ? 3 : posts.length;
  const visiblePosts = sortedPosts.slice(0, maxVisible);
  const remainingCount = posts.length - maxVisible;

  return (
    <div
      ref={setNodeRef}
      data-calendar-day={format(date, 'yyyy-MM-dd')}
      role="button"
      tabIndex={0}
      onClick={() => onClick(date)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(date);
        }
      }}
      className={cn(
        // Base styles
        'group relative w-full aspect-square p-1 sm:p-1.5',
        'flex flex-col',
        'border border-gray-100 rounded-lg',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',

        // Current month vs other months
        isCurrentMonth ? 'bg-white' : 'bg-gray-50',

        // Hover state (when not dragging)
        !isDraggingActive && isCurrentMonth && 'hover:bg-gray-50 hover:border-gray-200',

        // Selected state
        isSelected && 'ring-2 ring-primary-500 bg-primary-50 border-primary-200',

        // Today highlight
        isToday && !isSelected && !isHoveringOver && 'border-primary-300 bg-primary-25',

        // Past date styling when dragging (disabled drop target)
        isDraggingActive && isPast && 'opacity-50 cursor-not-allowed',

        // Drag-and-drop states (only for valid, non-past dates)
        isValidDropTarget && !isHoveringOver && 'border-dashed border-2 border-primary-300',
        isHoveringOver && 'bg-primary-100 border-2 border-primary-500 ring-2 ring-primary-300 scale-105'
      )}
      aria-label={`${date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })}${postCount > 0 ? `, ${t('calendar_aria_date_posts', { count: postCount })}` : ''}`}
    >
      {/* Header row: Date + Moon + Add button */}
      <div className="flex items-center justify-between">
        {/* Date Number */}
        <div className="flex items-center gap-0.5">
          <span
            className={cn(
              'text-xs sm:text-sm font-medium leading-none',
              isToday
                ? 'w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center rounded-full bg-primary-500 text-primary-contrast text-[10px] sm:text-xs'
                : isCurrentMonth
                  ? 'text-gray-900'
                  : 'text-gray-400',
              isHoveringOver && !isToday && 'text-primary-700 font-bold'
            )}
          >
            {dayNumber}
          </span>

          {/* Moon phase indicator */}
          {showMoonPhases && isCurrentMonth && (
            <MoonPhaseIndicator date={date} size="sm" className="opacity-70" />
          )}
        </div>

        {/* Quick action button - visible on hover; available on every day cell
            so outside-month days are addable too (and headers align) */}
        <QuickAddMenu
          date={date}
          onAddPost={onAddPost}
          onAddCampaign={onAddCampaign}
          onAddContentPillar={onAddContentPillar}
          menuPosition="right"
        />
      </div>

      {/* Campaigns and Holidays stacked */}
      {(campaigns.length > 0 || holidays.length > 0) && (
        <div className="space-y-0.5 w-full my-0.5">
          {/* Campaign badges — slot-ordered with spacers for consistent vertical position */}
          {(() => {
            if (campaigns.length === 0) return null;

            // Build slot-ordered array with spacers
            const maxSlot = campaigns.reduce((max, c) => {
              const slot = campaignSlots?.get(c.id) ?? 0;
              return slot > max ? slot : max;
            }, 0);

            const slotArray: Array<CalendarNote | null> = new Array(maxSlot + 1).fill(null);
            for (const c of campaigns) {
              const slot = campaignSlots?.get(c.id) ?? 0;
              slotArray[slot] = c;
            }

            return slotArray.map((campaign, slotIdx) => {
              if (!campaign) {
                // Empty spacer to maintain vertical alignment
                return <div key={`spacer-${slotIdx}`} className="text-[10px] py-0.5 invisible">{'\u00A0'}</div>;
              }

              const position = campaignPositions?.get(campaign.id) ?? 'single';
              const isFirst = position === 'first' || position === 'single';
              const isLast = position === 'last' || position === 'single';
              const isMiddle = position === 'middle';

              return (
                <Tooltip
                  key={campaign.id}
                  content={
                    <div>
                      <div>
                        <span className="font-medium">{campaign.repeatType ? t('content_pillar', { defaultValue: 'Content Pillar' }) : t('campaign')}:</span> {campaign.title}
                        {campaign.repeatType && (
                          <span className="text-gray-300 ml-1">
                            ({t(`repeat_${campaign.repeatType.toLowerCase()}`)})
                          </span>
                        )}
                      </div>
                      {campaign.description && (
                        <div className="text-gray-300 text-xs mt-1">{campaign.description}</div>
                      )}
                      {calendarsById?.get(campaign.calendarId)?.name && (
                        <div className="text-gray-400 text-xs mt-0.5">{calendarsById.get(campaign.calendarId)?.name}</div>
                      )}
                    </div>
                  }
                >
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      onCampaignClick?.(campaign);
                    }}
                    className={cn(
                      'text-[10px] py-0.5 font-medium text-left transition-opacity',
                      isFirst && isLast && 'rounded px-1.5',
                      isFirst && !isLast && 'rounded-l px-1.5 -mr-2 sm:-mr-3',
                      isLast && !isFirst && 'rounded-r px-1.5 -ml-2 sm:-ml-3',
                      isMiddle && 'rounded-none px-1.5 -mx-2 sm:-mx-3',
                      'truncate',
                      onCampaignClick && 'cursor-pointer hover:opacity-80'
                    )}
                    style={{
                      backgroundColor: campaign.color || '#3B82F6',
                      color: getContrastColor(campaign.color || '#3B82F6'),
                    }}
                  >
                    {campaign.title}
                  </div>
                </Tooltip>
              );
            });
          })()}

          {/* Holiday badges */}
          {holidays.map((holiday) => (
            <Tooltip
              key={holiday.id}
              content={
                <div>
                  <span className="font-medium">{t('holiday')}:</span> {holiday.localName}
                  {holiday.countryName && (
                    <span className="text-gray-300 ml-1">({holiday.countryName})</span>
                  )}
                </div>
              }
            >
              <div
                className="text-[10px] px-1.5 py-0.5 rounded truncate font-medium text-left"
                style={{
                  backgroundColor: holiday.colorCode || '#FFD700',
                  color: getContrastColor(holiday.colorCode || '#FFD700'),
                }}
              >
                {holiday.localName}
              </div>
            </Tooltip>
          ))}
        </div>
      )}

      {/* Post dots (mobile) */}
      {hasContent && (
        <div className="flex-1 mt-1 sm:hidden">
          <div className="flex items-center gap-1 justify-center">
            {posts.slice(0, 2).map((post) => (
              <span
                key={post.source === 'blog' ? `blog-${post.blogPostId}-dot` : `${post.id}-${post.mappingId || 'unmapped'}-dot`}
                className="w-1.5 h-1.5 rounded-full bg-primary-500"
              />
            ))}
            {posts.length > 2 && (
              <span className="text-[8px] leading-none text-gray-500 font-medium">
                +{posts.length - 2}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Post thumbnails (tablet+) */}
      {hasContent && (
        <div className="flex-1 mt-1 hidden sm:block">
          <div className="flex flex-wrap gap-1">
            {visiblePosts.map((post) => (
              <MiniPostThumb
                key={post.source === 'blog' ? `blog-${post.blogPostId}` : `${post.id}-${post.mappingId || 'unmapped'}`}
                post={post}
                date={date}
                formatTime={formatTimeOnly}
                isDraggingGlobal={isDraggingActive}
                onPostClick={onPostClick}
                onPostDelete={onPostDelete}
              />
            ))}
            {remainingCount > 0 && (
              <div className="w-9 h-9 md:w-11 md:h-11 rounded bg-gray-200 flex items-center justify-center text-[10px] md:text-xs font-semibold text-gray-600">
                +{remainingCount}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Drop indicator icon when hovering */}
      {isHoveringOver && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-primary-500 text-primary-contrast rounded-full p-1 shadow-lg animate-pulse">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m0 0l-4-4m4 4l4-4"
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Empty cell for padding (days from previous/next month)
 */
export const EmptyDayCell = () => (
  <div className="w-full aspect-square p-2 border border-transparent" aria-hidden="true" />
);
